import { Box, Button, Card, CardContent, Grid, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { useState } from "react";

import { ErrorState, LoadingState, PageHeader } from "../components";
import { useMetrics } from "../console_tenant_console/useData";
import { formatMoney } from "../i18n/format";
import help from "./HELP.md?raw";

function Stat({ label, value }: { label: string; value: string | number | null | undefined }) {
  return <Card sx={{ height: "100%" }}><CardContent><Typography variant="caption" color="text.secondary">{label}</Typography><Typography variant="h4">{value ?? "—"}</Typography></CardContent></Card>;
}

type MetricsData = { mrr: string; arr: string; organizations: number; active_users_30d: number; trials_started: number; conversions: number; conversion_rate: number | null; churned: number; by_status: Record<string, number>; by_plan: Record<string, number>; module_adoption: Record<string, number>; signups_by_week: { week: string; count: number }[] };

export function Metrics() {
  const [days, setDays] = useState(90);
  const m = useMetrics(days);
  if (m.isLoading) return <LoadingState />;
  if (m.isError || !m.data) return <ErrorState message={m.error?.message ?? ""} />;
  const d = m.data as unknown as MetricsData;
  const max = Math.max(1, ...d.signups_by_week.map((w) => w.count));
  return (
    <>
      <PageHeader help={help} title="SaaS metrics" crumbs={[{ label: "Console" }, { label: "Metrics" }]}
        actions={<Stack direction="row" spacing={1}>
          <TextField select size="small" value={days} onChange={(e) => setDays(Number(e.target.value))}>{[30, 90, 180, 365].map((n) => <MenuItem key={n} value={n}>Last {n} days</MenuItem>)}</TextField>
          <Button href={`/api/v1/console/metrics/?days=${days}&file=csv`}>Export CSV</Button>
        </Stack>} />
      <Grid container spacing={2}>
        <Grid size={{ xs: 6, md: 3 }}><Stat label="MRR" value={formatMoney(d.mrr, "USD", 0)} /></Grid>
        <Grid size={{ xs: 6, md: 3 }}><Stat label="ARR" value={formatMoney(d.arr, "USD", 0)} /></Grid>
        <Grid size={{ xs: 6, md: 3 }}><Stat label="Organizations" value={d.organizations} /></Grid>
        <Grid size={{ xs: 6, md: 3 }}><Stat label="Active users (30d)" value={d.active_users_30d} /></Grid>
        <Grid size={{ xs: 6, md: 3 }}><Stat label="Trials started" value={d.trials_started} /></Grid>
        <Grid size={{ xs: 6, md: 3 }}><Stat label="Conversions" value={d.conversions} /></Grid>
        <Grid size={{ xs: 6, md: 3 }}><Stat label="Conversion rate" value={d.conversion_rate != null ? `${Math.round(d.conversion_rate * 100)}%` : "—"} /></Grid>
        <Grid size={{ xs: 6, md: 3 }}><Stat label="Churned" value={d.churned} /></Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card><CardContent>
            <Typography variant="subtitle2" gutterBottom>Signups by week</Typography>
            <Stack direction="row" alignItems="flex-end" spacing={0.5} sx={{ height: 140 }}>
              {d.signups_by_week.map((w) => <Stack key={w.week} alignItems="center" sx={{ flex: 1 }} title={`${w.week}: ${w.count}`}><Box sx={{ width: "100%", height: `${(w.count / max) * 120}px`, bgcolor: "primary.main", borderRadius: "3px" }} /><Typography variant="caption">{w.count}</Typography></Stack>)}
            </Stack>
          </CardContent></Card>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card sx={{ height: "100%" }}><CardContent><Typography variant="subtitle2" gutterBottom>By status</Typography>{Object.entries(d.by_status).map(([k, v]) => <Typography key={k} variant="body2">{k}: <b>{v}</b></Typography>)}
            <Typography variant="subtitle2" sx={{ mt: 1 }} gutterBottom>By plan</Typography>{Object.entries(d.by_plan).map(([k, v]) => <Typography key={k} variant="body2">{k}: <b>{v}</b></Typography>)}</CardContent></Card>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card sx={{ height: "100%" }}><CardContent><Typography variant="subtitle2" gutterBottom>Module adoption</Typography>{Object.entries(d.module_adoption).map(([k, v]) => <Typography key={k} variant="body2">{k}: <b>{v}</b></Typography>)}</CardContent></Card>
        </Grid>
      </Grid>
    </>
  );
}
